import {
  AlertCircle,
  ExternalLink,
  Maximize2,
  MessageCircle,
  Minimize2,
  Minus,
  MoveDiagonal2,
  Phone,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMatch } from 'react-router-dom';
import { toast } from 'sonner';

import { formatPhoneNumber } from '@/components/audio-conversation/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { useGetRCOAuthStatusQuery } from '@/services/api/ringcentralService';
import type { MatterClient } from '@/services/types/matter-client-types';
import type { User } from '@/services/types/user-types';

interface RingCentralMiniDialerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ContactOption = {
  id: string;
  name: string;
  roleLabel: string;
  phoneRaw: string;
  phoneDisplay: string;
};

const MANAGING_PARALEGAL_PLACEHOLDER = '{{Managing Paralegal Name}}';
const FIRM_NAME_PLACEHOLDER = '{{Firm Name}}';

const MIN_WIDTH = 720;
const MIN_HEIGHT = 520;
const MAX_WIDTH = 1600;
const MAX_HEIGHT = 1040;

export const RingCentralMiniDialerDrawer = ({
  open,
  onOpenChange,
}: RingCentralMiniDialerDrawerProps) => {
  const [panelState, setPanelState] = useState<
    'panel' | 'fullscreen' | 'minimized'
  >('panel');
  const [panelSize, setPanelSize] = useState({ width: 1400, height: 920 });
  const [isDesktop, setIsDesktop] = useState(false);
  const resizeAbortRef = useRef<(() => void) | null>(null);
  const matterMatchWithTab = useMatch('/matters/:matter_id/*');
  const matterMatchExact = useMatch('/matters/:matter_id');
  const activeMatterId =
    matterMatchWithTab?.params?.matter_id ||
    matterMatchExact?.params?.matter_id;

  const { data: matter, isFetching: isMatterLoading } = useGetMatterQuery(
    activeMatterId || '',
    { skip: !activeMatterId || !open },
  );

  const { data: rcOAuthStatus } = useGetRCOAuthStatusQuery(undefined, {
    skip: !open,
  });
  const isRCConnected = rcOAuthStatus?.connected ?? false;

  const firmName = useMemo(() => {
    if (matter?.firm?.name?.trim()) {
      return matter.firm.name.trim();
    }
    const altName = (matter?.firm as { full_name?: string } | undefined)
      ?.full_name;
    return altName?.trim() || null;
  }, [matter?.firm]);

  const managingParalegalFirstName = useMemo(() => {
    const mp = matter?.firm?.managing_paralegal as
      | (User & { display_name?: string; full_name?: string })
      | null
      | undefined;
    if (!mp) return null;
    const direct = mp.first_name?.trim();
    if (direct) {
      return direct.split(' ')[0] || direct;
    }
    const fallbackName =
      mp.display_name?.trim() || mp.full_name?.trim() || mp.username?.trim();
    if (!fallbackName) return null;
    return fallbackName.split(' ')[0] || fallbackName;
  }, [matter?.firm?.managing_paralegal]);

  const templateParalegalName =
    managingParalegalFirstName || MANAGING_PARALEGAL_PLACEHOLDER;
  const templateFirmName = firmName || FIRM_NAME_PLACEHOLDER;

  const smsTemplates = useMemo(
    () => [
      {
        id: 'check-in-known',
        label: 'Client check-in (existing contact)',
        body: `Hey \u2013 it\u2019s ${templateParalegalName} from ${templateFirmName}. Just wanted to check in and see how you\u2019re feeling? You can also reach me at 203-490-6995.`,
      },
      {
        id: 'check-in-new',
        label: 'Client check-in (new intro)',
        body: `Good afternoon. It\u2019s ${templateParalegalName} from ${templateFirmName} (the paralegal assigned to your case).
Wanted to share my number via text: 203-490-6995.
When you get a moment, could you please send over the following?
- Photos of your health insurance card (front/back)
- Any lost wages due to this accident
- Photo of your driver's license
- Photos of any property damage
- Photo of your auto insurance card
I will be in touch \u2013 thank you!
${templateParalegalName}
${templateFirmName}`,
      },
      {
        id: 'photos-follow-up',
        label: 'Photo/documents follow-up',
        body: `Hello, this is ${templateParalegalName} from ${templateFirmName} checking in. When you get a moment, could you please send over the following?
- Photos of your health insurance card (front/back)
- Any lost wages due to this accident
- Photo of your driver's license
- Photos of any property damage
- Photo of your auto insurance card
Thank you!
${templateParalegalName}
${templateFirmName}`,
      },
      {
        id: 'lost-wages',
        label: 'Lost wages details',
        body: `Hello, this is ${templateParalegalName} from ${templateFirmName} checking in. Could you let us know how many days of work you missed due to the incident? Thank you!`,
      },
      {
        id: 'property-damage',
        label: 'Property damage status',
        body: `Hi, just wanted to check in regarding property damage. What\u2019s the status on the vehicle repairs or estimates? Let me know here so we can keep things moving.`,
      },
    ],
    [templateFirmName, templateParalegalName],
  );

  const contacts = useMemo<ContactOption[]>(() => {
    if (!matter) return [];
    type ExtendedMatterClient = MatterClient & { is_primary?: boolean };
    const matterClients = (matter.clients ?? []) as ExtendedMatterClient[];
    return matterClients.flatMap((client) => {
      const numbers = client.phone_numbers ?? [];
      if (!numbers.length) return [];
      const fallbackName =
        `${client.first_name || ''} ${client.last_name || ''}`.trim();
      const displayName = client.name || fallbackName || 'Client';
      const roleLabel = client.is_primary ? 'Primary client' : 'Client';
      return numbers.map((phone) => ({
        id: `${client.id}-${phone.id}`,
        name: displayName,
        roleLabel,
        phoneRaw: phone.phone_number || '',
        phoneDisplay: formatPhoneNumber(phone.phone_number || ''),
      }));
    });
  }, [matter]);

  const handleCopyTemplate = useCallback((body: string) => {
    if (!navigator?.clipboard) {
      toast.error('Clipboard access is not available in this browser.');
      return;
    }
    navigator.clipboard
      .writeText(body)
      .then(() => {
        toast.success('Template copied to clipboard.');
      })
      .catch(() => {
        toast.error('Unable to copy text.');
      });
  }, []);

  useEffect(() => {
    if (open) {
      setPanelState('panel');
    }
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const clampToViewport = () => {
      if (!open || panelState !== 'panel') return;
      const viewportWidth = window.innerWidth - 48;
      const viewportClampHeight = window.innerHeight - 96;
      setPanelSize((current) => ({
        width: Math.min(
          Math.max(current.width, MIN_WIDTH),
          Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportWidth)),
        ),
        height: Math.min(
          Math.max(current.height, MIN_HEIGHT),
          Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, viewportClampHeight)),
        ),
      }));
    };
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [open, panelState]);

  useEffect(() => {
    return () => {
      if (resizeAbortRef.current) {
        resizeAbortRef.current();
      }
    };
  }, []);

  const handleClose = () => {
    setPanelState('panel');
    onOpenChange(false);
  };

  const handleRCConnect = useCallback(() => {
    const backendHost =
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000';
    const origin = encodeURIComponent(window.location.origin);
    const redirectTo = encodeURIComponent(window.location.href);
    window.location.href = `${backendHost}/api/integrations/ringcentral/oauth/connect?origin=${origin}&redirect_to=${redirectTo}`;
  }, []);

  if (!open) return null;

  const renderHeader = () => (
    <div className="flex flex-col gap-2 border-b px-5 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-zinc-900">
            Client communications
          </p>
          <p className="text-xs text-zinc-500">
            Text or call clients via RingCentral without leaving the matter.
          </p>
        </div>
        <div className="flex items-center gap-1 text-zinc-500">
          <button
            type="button"
            onClick={() => setPanelState('minimized')}
            className="rounded-md p-1.5 hover:bg-zinc-100"
            aria-label="Minimize"
          >
            <Minus className="h-4 w-4" />
          </button>
          {panelState === 'fullscreen' ? (
            <button
              type="button"
              onClick={() => setPanelState('panel')}
              className="rounded-md p-1.5 hover:bg-zinc-100"
              aria-label="Exit fullscreen"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setPanelState('fullscreen')}
              className="rounded-md p-1.5 hover:bg-zinc-100"
              aria-label="Fullscreen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge
          variant="secondary"
          className="bg-emerald-100 text-emerald-900"
        >
          RingCentral
        </Badge>
      </div>
    </div>
  );



  const renderContactsPanel = (inline: boolean) => (
    <div
      className={`flex min-h-0 flex-col gap-4 ${
        inline ? 'h-full overflow-y-auto pr-3' : ''
      }`}
      style={inline ? { scrollbarWidth: 'thin' } : undefined}
    >
      {!isRCConnected && (
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">
                RingCentral not connected
              </p>
              <p className="text-xs text-orange-700 mt-0.5">
                Connect your account to make calls and send SMS through Finch.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRCConnect}
                className="mt-2 h-7 text-xs border-orange-300 text-orange-800 hover:bg-orange-100"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Connect RingCentral
              </Button>
            </div>
          </div>
        </div>
      )}

      {!activeMatterId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Select a matter to view client contacts.
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-zinc-900">Matter contacts</p>
            <p className="text-xs text-zinc-500">
              {isMatterLoading
                ? 'Loading contacts\u2026'
                : `${contacts.length} reachable number${contacts.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        {contacts.length === 0 && !isMatterLoading ? (
          <div className="rounded-md border border-dashed px-3 py-4 text-xs text-zinc-500">
            No client phone numbers on file. Add numbers to the matter to
            contact clients.
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((contact) => (
              <div key={contact.id} className="rounded-md border px-3 py-2">
                <p className="text-sm font-medium text-zinc-900">
                  {contact.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {contact.roleLabel} • {contact.phoneDisplay || 'No number'}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <p className="text-sm font-medium text-zinc-900">SMS templates</p>
          <p className="text-xs text-zinc-500">
            Copy a snippet, personalize it, then paste in RingCentral.
          </p>
        </div>
        <div className="grid gap-2">
          {smsTemplates.map((template) => (
            <div key={template.id} className="rounded-md border px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-zinc-900">
                  {template.label}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopyTemplate(template.body)}
                  className="text-xs"
                >
                  Copy
                </Button>
              </div>
              <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-zinc-600">
                {template.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  const renderCallPanel = (inline: boolean) => (
    <div className={`flex flex-col gap-4 ${inline ? 'h-full' : ''}`}>
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-zinc-500" />
          <p className="text-sm font-medium text-zinc-900">RingCentral</p>
        </div>
        {isRCConnected ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-6 text-center">
            <Phone className="mx-auto mb-3 h-8 w-8 text-emerald-600" />
            <p className="text-sm font-medium text-zinc-900">
              Connected as {rcOAuthStatus?.extension_name || 'RingCentral User'}
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              {rcOAuthStatus?.extension_number
                ? `Ext. ${rcOAuthStatus.extension_number} \u00B7 `
                : ''}
              Calls and SMS use your personal RingCentral account.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-6 text-center">
            <Phone className="mx-auto mb-3 h-8 w-8 text-orange-500" />
            <p className="text-sm font-medium text-zinc-900">
              Connect your RingCentral account
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              Authorize Finch to make calls and send SMS on your behalf.
            </p>
            <Button
              size="sm"
              onClick={handleRCConnect}
              className="mt-3 bg-orange-600 hover:bg-orange-700"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Connect RingCentral
            </Button>
          </div>
        )}
      </section>
    </div>
  );

  const renderBody = () => {
    const stackedLayout = (
      <div className="flex h-full min-h-0 flex-col gap-5">
        {renderContactsPanel(false)}
        {renderCallPanel(false)}
      </div>
    );

    const resizableLayout = (
      <ResizablePanelGroup
        direction="horizontal"
        className="flex h-full min-h-0 gap-4"
      >
        <ResizablePanel
          defaultSize={38}
          minSize={26}
          maxSize={48}
          className="min-w-[280px]"
        >
          {renderContactsPanel(true)}
        </ResizablePanel>
        <ResizableHandle
          withHandle
          className="relative mx-1 w-[3px] rounded-full bg-zinc-200"
        />
        <ResizablePanel defaultSize={62} minSize={52} className="min-w-0">
          {renderCallPanel(true)}
        </ResizablePanel>
      </ResizablePanelGroup>
    );

    return (
      <div className="flex-1 min-h-0 px-5 py-4">
        {isDesktop ? resizableLayout : stackedLayout}
      </div>
    );
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (panelState !== 'panel' || typeof window === 'undefined') return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const { width: startWidth, height: startHeight } = panelSize;
    const viewportWidth = window.innerWidth - 48;
    const viewportClampHeight = window.innerHeight - 96;
    const maxWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportWidth));
    const maxHeight = Math.max(
      MIN_HEIGHT,
      Math.min(MAX_HEIGHT, viewportClampHeight),
    );

    const onPointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      const diagonalChange = (-deltaX - deltaY) / 2;

      const nextWidth = Math.min(
        Math.max(startWidth + diagonalChange, MIN_WIDTH),
        maxWidth,
      );
      const nextHeight = Math.min(
        Math.max(startHeight + diagonalChange, MIN_HEIGHT),
        maxHeight,
      );

      setPanelSize({ width: nextWidth, height: nextHeight });
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      resizeAbortRef.current = null;
    };

    resizeAbortRef.current = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const renderPanel = () => (
    <div
      className={`fixed z-50 rounded-xl border border-zinc-200 bg-white shadow-2xl transition-all duration-200 ${
        panelState === 'panel' ? 'right-6 bottom-6' : 'inset-4'
      }`}
      style={
        panelState === 'panel'
          ? {
              width: panelSize.width,
              height: panelSize.height,
              maxWidth: '95vw',
            }
          : undefined
      }
    >
      <div className="relative flex h-full flex-col">
        {renderHeader()}
        {renderBody()}
        {panelState === 'panel' && (
          <button
            type="button"
            aria-label="Resize client communications"
            className="absolute left-4 top-4 flex h-7 w-7 cursor-nwse-resize items-center justify-center rounded-md border border-zinc-300 bg-white text-zinc-600 shadow-sm transition hover:bg-zinc-50"
            onPointerDown={handleResizeStart}
          >
            <MoveDiagonal2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  const renderFullscreenOverlay = () => (
    <div
      className="fixed inset-0 z-40 bg-black/60"
      role="button"
      tabIndex={0}
      aria-label="Exit fullscreen communications"
      onClick={() => setPanelState('panel')}
      onKeyDown={(event) => {
        if (
          event.key === 'Escape' ||
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          setPanelState('panel');
        }
      }}
    />
  );

  const renderMinimized = () => (
    <div className="fixed bottom-4 right-4 z-40">
      <div className="flex items-center gap-3 rounded-full border border-zinc-200 bg-white px-4 py-2 shadow-lg">
        <MessageCircle className="h-4 w-4 text-emerald-600" />
        <span className="text-sm font-medium text-zinc-800">
          Client communications
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPanelState('panel')}
            className="rounded-md p-1 hover:bg-zinc-100"
            aria-label="Open communications"
          >
            <Maximize2 className="h-4 w-4 text-zinc-600" />
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1 hover:bg-zinc-100"
            aria-label="Close communications"
          >
            <X className="h-4 w-4 text-zinc-600" />
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {panelState === 'fullscreen' && renderFullscreenOverlay()}
      {panelState === 'minimized' ? renderMinimized() : renderPanel()}
    </>,
    document.body,
  );
};
